import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { articleQuickLinks } from '@/mocks/articles';

interface ArticleLayoutProps {
  title: string;
  description: string;
  heroImage: string;
  heroAlt: string;
  breadcrumbLabel: string;
  children: ReactNode;
  schemaMarkup?: string;
}

export default function ArticleLayout({
  title,
  description,
  heroImage,
  heroAlt,
  breadcrumbLabel,
  children,
  schemaMarkup,
}: ArticleLayoutProps) {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className="min-h-screen bg-background-50">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-secondary-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2 no-underline" onClick={scrollToTop}>
              <span className="text-lg font-bold tracking-tight text-foreground-950 whitespace-nowrap">
                OOPBUY <span className="text-accent-500">Spreadsheet</span>
              </span>
            </a>
            <div className="hidden md:flex items-center gap-4">
              <a href="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
                Startseite
              </a>
              <div className="relative group">
                <button className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer">
                  Shopping Guide
                  <i className="ri-arrow-down-s-line text-xs" />
                </button>
                <div className="absolute top-full left-0 mt-1 bg-white border border-secondary-200 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30 min-w-[220px]">
                  {articleQuickLinks.map((link) => (
                    <button
                      key={link.path}
                      onClick={() => { navigate(link.path); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                oopbuy spreadsheet
              </a>
            </div>
            <div className="flex md:hidden items-center gap-2">
              <a href="/" className="text-xs text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
                Startseite
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                oopbuy spreadsheet
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-6 pb-2">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap" aria-label="Breadcrumb">
          <a href="/" className="hover:text-foreground-800 transition-colors whitespace-nowrap">Startseite</a>
          <span>/</span>
          <span className="text-foreground-800 font-medium whitespace-nowrap">{breadcrumbLabel}</span>
        </nav>
      </div>

      {/* Article */}
      <article className="max-w-[1440px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Hero image */}
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={heroImage}
              alt={heroAlt}
              className="w-full aspect-[16/7] object-cover object-top"
            />
          </div>

          {/* Meta */}
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full">
              {breadcrumbLabel.split(' – ')[0] || 'Artikel'}
            </span>
            <span className="text-xs text-foreground-500">Aktualisiert: Juli 2026</span>
          </div>

          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
            {title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-10">
            {description}
          </p>

          {children}

          {/* Internal links to other articles */}
          <div className="mt-16 bg-background-100 rounded-2xl p-8">
            <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">
              Weitere hilfreiche Artikel
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {articleQuickLinks
                .filter((link) => !breadcrumbLabel.includes(link.label))
                .slice(0, 6)
                .map((link) => (
                  <button
                    key={link.path}
                    onClick={() => { navigate(link.path); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="flex items-center gap-2 text-left px-4 py-3 rounded-xl bg-background-50 hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-circle-line text-accent-500 text-lg flex-shrink-0" />
                    <span className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors">
                      {link.label}
                    </span>
                  </button>
                ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-10 text-center mt-10">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">
              Bereit, das OOPBUY Spreadsheet selbst zu entdecken?
            </h3>
            <p className="text-secondary-400 text-sm mb-6 max-w-md mx-auto">
              Starte jetzt und finde die besten Produkte zu den günstigsten Preisen im OOPBUY Spreadsheet.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-white hover:bg-secondary-100 text-foreground-950 px-7 py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
              >
                oopbuy spreadsheet
              </a>
              <a
                href="/"
                className="border border-white/30 hover:border-white/60 text-white px-7 py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
              >
                Zurück zur Startseite
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-foreground-950 mt-12">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-secondary-500 text-xs">
              &copy; {new Date().getFullYear()} oopbuyspread.top – Eine unabhängige Informationsplattform
            </p>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <a href="/" className="text-secondary-500 hover:text-white text-xs transition-colors whitespace-nowrap">
                Startseite
              </a>
              {articleQuickLinks.slice(0, 4).map((link) => (
                <button
                  key={link.path}
                  onClick={() => { navigate(link.path); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="text-secondary-500 hover:text-white text-xs transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </button>
              ))}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-secondary-500 hover:text-white text-xs transition-colors whitespace-nowrap"
              >
                oopbuy spreadsheet
              </a>
            </div>
          </div>
        </div>
      </footer>

      {schemaMarkup && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaMarkup }} />
      )}
    </main>
  );
}